/**
 * Field validation for `LoginForm.tsx`. The only rule is "not blank": the Users API decides
 * whether the credentials are right.
 */

/**
 * Enum for Login input field names
 */
export enum LoginInputFields {
	userName = "userName",
	password = "password",
}

/**
 * Enum for Login input types. Only {@link LoginInputTypeMap} uses it.
 */
export enum LoginInputTypes {
	Input = "Input",
}

/**
 * Mapping of input field names to their types. No file imports it.
 */
export const LoginInputTypeMap = {
	[LoginInputFields.userName]: LoginInputTypes.Input,
	[LoginInputFields.password]: LoginInputTypes.Input,
};

/**
 * Interface for validation function parameters
 */
export interface LoginValidationProps {
	inputName: LoginInputFields;
	value: string;
}

/**
 * True when `value` is not blank after trimming. `inputName` is not read: both fields have the
 * same rule.
 * @param param - The input validation parameters
 * @returns A boolean indicating if the input is valid
 */
export function isLoginInputValid({ inputName, value }: LoginValidationProps): boolean {
	// Both fields are required - just check if they have a value
	return !!value?.trim();
}

/**
 * Check if all form inputs are valid before submission: both are non-blank. `LoginForm` then
 * sends the values as typed, untrimmed.
 * @param userName - Username value
 * @param password - Password value
 * @returns A boolean indicating if all inputs are valid
 */
export const checkAllInputsValid = (userName: string, password: string): boolean => {
	const userNameValid = isLoginInputValid({
		inputName: LoginInputFields.userName,
		value: userName,
	});

	const passwordValid = isLoginInputValid({
		inputName: LoginInputFields.password,
		value: password,
	});

	return userNameValid && passwordValid;
};
